import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { MobilePage, MobilePageHeader, mobile } from '../components/mobile'
import { PlusIcon, XIcon, UsersIcon } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { AddressAutocomplete } from '../components/AddressAutocomplete'
import {
  logActivity,
  ActivityTypes,
  getEntityActivities,
  ActivityLog,
  formatActivityMeta,
} from '../utils/activityLogger'
import { PropertyPaymentFields } from '../components/property/PropertyPaymentFields'
import { PropertyAssignmentFields } from '../components/property/PropertyAssignmentFields'
import { reapplyAssignmentDefaultsForProperty } from '../api/jobAssignmentDefaults'
import type { CustomerPaymentDefaults } from '../components/property/types'
import { parseEntityNotes } from '../lib/entityNotes'
import { buildUpdateDescription, diffSnapshots } from '../lib/activityChangeSummary'

interface Customer {
  id: string
  company_name: string
}

interface PropertyNote {
  timestamp: string
  note: string
}

interface AdditionalAccess {
  id: string
  location: string
  access_type: string
  access_info: string
}

export default function PropertyEdit() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [customers, setCustomers] = useState<Customer[]>([])
  const [loading, setLoading] = useState(false)
  const [manualLat, setManualLat] = useState('')
  const [manualLng, setManualLng] = useState('')
  const [geocodePreview, setGeocodePreview] = useState<{
    latitude: number
    longitude: number
    matched_address?: string | null
  } | null>(null)
  const [coordsRequired, setCoordsRequired] = useState(false)
  const [activeTab, setActiveTab] = useState<'address' | 'info' | 'payment' | 'contacts' | 'notes'>('address')

  // Property Information
  const [propertyData, setPropertyData] = useState({
    customer_id: '',
    name: '',
    address: '',
    address_line_2: '',
    city: '',
    state: '',
    zip: '',
    building_type: '',
    units: '',
    stories: '',
    access_type: '',
    access_info: '',
    payment_method: '',
    sales_tax_status: '',
    default_assigned_crew_id: '',
    default_assigned_employee_id: '',
  })

  const [additionalAccess, setAdditionalAccess] = useState<AdditionalAccess[]>([])
  const [notes, setNotes] = useState('')
  const [propertyNotes, setPropertyNotes] = useState<PropertyNote[]>([])
  const [activities, setActivities] = useState<ActivityLog[]>([])
  const [customerContacts, setCustomerContacts] = useState<any[]>([])
  const [selectedContactIds, setSelectedContactIds] = useState<Set<string>>(new Set())
  const [showCreateContactModal, setShowCreateContactModal] = useState(false)
  const [newContact, setNewContact] = useState({
    name: '',
    title: '',
    phone: '',
    cell: '',
    email: '',
  })
  const [creatingContact, setCreatingContact] = useState(false)
  const [customerDefaults, setCustomerDefaults] = useState<CustomerPaymentDefaults | null>(null)
  const initialSnapshotRef = useRef<Record<string, unknown> | null>(null)
  const [notification, setNotification] = useState<{
    message: string
    type: 'success' | 'error'
  } | null>(null)

  const buildingTypes = ['Residential', 'Commercial', 'Office', 'Other']
  const accessTypes = ['Keypad', 'Key', 'Keyfob', 'Lockbox', 'Numberlock', 'Other']

  const PROPERTY_UPDATE_LABELS: Record<string, string> = {
    name: 'Property Name',
    address: 'Address',
    city: 'City',
    state: 'State',
    zip: 'Zip',
    building_type: 'Building Type',
    units: 'Units',
    stories: 'Stories',
    access_type: 'Access Type',
    access_info: 'Access Info',
    payment_method: 'Payment Method',
    sales_tax_status: 'Sales Tax Status',
    customer_id: 'Customer',
    notes_count: 'Notes',
    contacts_count: 'Linked Contacts',
  }

  const buildPropertySnapshot = (
    data: typeof propertyData,
    notesList: PropertyNote[],
    contactCount: number
  ): Record<string, unknown> => ({
    name: data.name,
    address: data.address,
    city: data.city,
    state: data.state,
    zip: data.zip,
    building_type: data.building_type,
    units: data.units,
    stories: data.stories,
    access_type: data.access_type,
    access_info: data.access_info,
    payment_method: data.payment_method || 'Customer Default',
    sales_tax_status: data.sales_tax_status || 'Customer Default',
    customer_id: data.customer_id,
    notes_count: notesList.length,
    contacts_count: contactCount,
  })

  useEffect(() => {
    fetchCustomers()
    if (id) fetchProperty()
  }, [id])

  useEffect(() => {
    if (id) {
      fetchActivities()
    }
  }, [id])

  // Fetch customer contacts when customer_id changes
  useEffect(() => {
    if (propertyData.customer_id) {
      fetchCustomerContacts(propertyData.customer_id)
      fetchCustomerDefaults(propertyData.customer_id)
    } else {
      setCustomerContacts([])
      setSelectedContactIds(new Set())
      setCustomerDefaults(null)
    }
  }, [propertyData.customer_id])

  // Load selected contacts when property is fetched
  useEffect(() => {
    if (id && propertyData.customer_id) {
      fetchPropertyContacts()
    }
  }, [id, propertyData.customer_id])

  const fetchCustomers = async () => {
    try {
      const { data, error } = await supabase
        .from('customers')
        .select('id, company_name')
        .order('company_name')
      if (error) throw error
      setCustomers(data || [])
    } catch (error) {
      console.error('Error fetching customers:', error)
      setNotification({
        type: 'error',
        message: 'Error loading customers: ' + (error as any).message,
      })
      setTimeout(() => setNotification(null), 5000)
    }
  }

  const fetchProperty = async () => {
    try {
      const { data: property, error: propertyError } = await supabase
        .from('properties')
        .select('*')
        .eq('id', id)
        .single()

      if (propertyError) throw propertyError

      setPropertyData({
        customer_id: property.customer_id || '',
        name: property.name || '',
        address: property.address || '',
        address_line_2: property.address_line_2 || '',
        city: property.city || '',
        state: property.state || '',
        zip: property.zip_code || '',
        // Use building_type if available, otherwise fall back to property_type
        building_type: property.building_type || property.property_type || '',
        units: property.units?.toString() || '',
        stories: property.stories?.toString() || '',
        access_type: property.access_type || '',
        access_info: property.access_info || '',
        payment_method: property.payment_method || '',
        sales_tax_status: property.sales_tax_status || '',
        default_assigned_crew_id: property.default_assigned_crew_id || '',
        default_assigned_employee_id: property.default_assigned_employee_id || '',
      })

      // Fetch notes if available
      const parsedNotes = parseEntityNotes(property.notes)
      setPropertyNotes(parsedNotes)

      initialSnapshotRef.current = buildPropertySnapshot(
        {
          customer_id: property.customer_id || '',
          name: property.name || '',
          address: property.address || '',
          address_line_2: property.address_line_2 || '',
          city: property.city || '',
          state: property.state || '',
          zip: property.zip_code || '',
          building_type: property.building_type || property.property_type || '',
          units: property.units?.toString() || '',
          stories: property.stories?.toString() || '',
          access_type: property.access_type || '',
          access_info: property.access_info || '',
          payment_method: property.payment_method || '',
          sales_tax_status: property.sales_tax_status || '',
          default_assigned_crew_id: property.default_assigned_crew_id || '',
          default_assigned_employee_id: property.default_assigned_employee_id || '',
        },
        parsedNotes,
        Array.isArray(property.contacts) ? property.contacts.length : 0
      )

      // Fetch additional access if available
      if (property.additional_access && Array.isArray(property.additional_access)) {
        const formattedAccess = property.additional_access.map((a: any, index: number) => ({
          id: `access-${index}`,
          location: a.location || '',
          access_type: a.access_type || '',
          access_info: a.access_info || '',
        }))
        setAdditionalAccess(formattedAccess)
      }

      // Load selected contacts if available
      if (property.contacts && Array.isArray(property.contacts)) {
        const contactIds = new Set<string>(property.contacts.map((c: any) => c.id).filter((id: any): id is string => Boolean(id)))
        setSelectedContactIds(contactIds)
      }
    } catch (error) {
      console.error('Error fetching property:', error)
      setNotification({
        type: 'error',
        message: 'Error loading property: ' + (error as any).message,
      })
      setTimeout(() => setNotification(null), 5000)
    }
  }

  const fetchCustomerContacts = async (customerId: string) => {
    try {
      const { data: customer, error } = await supabase
        .from('customers')
        .select('contacts')
        .eq('id', customerId)
        .single()

      if (error) throw error

      if (customer && customer.contacts && Array.isArray(customer.contacts)) {
        setCustomerContacts(customer.contacts)
      } else {
        setCustomerContacts([])
      }
    } catch (error) {
      console.error('Error fetching customer contacts:', error)
      setCustomerContacts([])
    }
  }

  const fetchCustomerDefaults = async (customerId: string) => {
    try {
      const { data: customer, error } = await supabase
        .from('customers')
        .select('payment_method, sales_tax_status')
        .eq('id', customerId)
        .single()

      if (error) throw error

      setCustomerDefaults({
        payment_method: customer?.payment_method || null,
        sales_tax_status: customer?.sales_tax_status || null,
      })
    } catch (error) {
      console.error('Error fetching customer defaults:', error)
      setCustomerDefaults(null)
    }
  }

  const fetchPropertyContacts = async () => {
    try {
      const { data: property, error } = await supabase
        .from('properties')
        .select('contacts')
        .eq('id', id!)
        .single()

      if (error) throw error

      if (property && property.contacts && Array.isArray(property.contacts)) {
        const contactIds = new Set(property.contacts.map((c: any) => c.id).filter(Boolean))
        setSelectedContactIds(contactIds)
      }
    } catch (error) {
      console.error('Error fetching property contacts:', error)
    }
  }

  const toggleContactSelection = (contactId: string) => {
    setSelectedContactIds((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(contactId)) {
        newSet.delete(contactId)
      } else {
        newSet.add(contactId)
      }
      return newSet
    })
  }

  const handleCreateContact = async () => {
    if (!propertyData.customer_id) {
      setNotification({
        type: 'error',
        message: 'Please select a customer first',
      })
      setTimeout(() => setNotification(null), 5000)
      return
    }

    if (!newContact.name.trim()) {
      setNotification({
        type: 'error',
        message: 'Contact name is required',
      })
      setTimeout(() => setNotification(null), 5000)
      return
    }

    setCreatingContact(true)
    try {
      // Fetch current customer contacts
      const { data: customer, error: fetchError } = await supabase
        .from('customers')
        .select('contacts')
        .eq('id', propertyData.customer_id)
        .single()

      if (fetchError) throw fetchError

      // Create new contact with ID
      const newContactWithId = {
        id: `contact_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        name: newContact.name.trim(),
        title: newContact.title.trim() || null,
        phone: newContact.phone.trim() || null,
        cell: newContact.cell.trim() || null,
        email: newContact.email.trim() || null,
      }

      // Add to customer contacts
      const currentContacts = (customer?.contacts && Array.isArray(customer.contacts)) ? customer.contacts : []
      const updatedContacts = [...currentContacts, newContactWithId]

      // Update customer
      const { error: updateError } = await supabase
        .from('customers')
        .update({ contacts: updatedContacts })
        .eq('id', propertyData.customer_id)

      if (updateError) throw updateError

      // Refresh customer contacts
      await fetchCustomerContacts(propertyData.customer_id)

      // Automatically select the new contact
      setSelectedContactIds((prev) => {
        const newSet = new Set(prev)
        newSet.add(newContactWithId.id)
        return newSet
      })

      // Reset form and close modal
      setNewContact({ name: '', title: '', phone: '', cell: '', email: '' })
      setShowCreateContactModal(false)

      setNotification({
        type: 'success',
        message: 'Contact added successfully and selected for this property!',
      })
      setTimeout(() => setNotification(null), 3000)
    } catch (error) {
      console.error('Error creating contact:', error)
      setNotification({
        type: 'error',
        message: 'Error creating contact: ' + (error as any).message,
      })
      setTimeout(() => setNotification(null), 5000)
    } finally {
      setCreatingContact(false)
    }
  }

  const fetchActivities = async () => {
    try {
      const acts = await getEntityActivities('property', id!)
      setActivities(acts)
    } catch (error) {
      console.error('Error fetching activities:', error)
    }
  }

  const addNote = () => {
    if (notes.trim()) {
      const newNote: PropertyNote = {
        timestamp: new Date().toISOString(),
        note: notes.trim(),
      }
      setPropertyNotes([newNote, ...propertyNotes])
      setNotes('')
    }
  }

  const removeNote = (timestamp: string) => {
    setPropertyNotes(propertyNotes.filter((n) => n.timestamp !== timestamp))
  }

  const handleAddressChange = (address: string) => {
    setPropertyData({ ...propertyData, address })
  }

  const handleAddressSelect = (addressData: any) => {
    setPropertyData({
      ...propertyData,
      address: addressData.address,
      city: addressData.city,
      state: addressData.state,
      zip: addressData.zip,
    })
  }

  const addAdditionalAccess = () => {
    const newAccess: AdditionalAccess = {
      id: Date.now().toString(),
      location: '',
      access_type: '',
      access_info: '',
    }
    setAdditionalAccess([...additionalAccess, newAccess])
  }

  const removeAdditionalAccess = (id: string) => {
    setAdditionalAccess(additionalAccess.filter((a) => a.id !== id))
  }

  const updateAdditionalAccess = (id: string, field: string, value: string) => {
    setAdditionalAccess(additionalAccess.map((a) => (a.id === id ? { ...a, [field]: value } : a)))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      if (!propertyData.customer_id) {
        setNotification({
          type: 'error',
          message: 'Please select a customer',
        })
        setTimeout(() => setNotification(null), 5000)
        setLoading(false)
        return
      }

      if (!propertyData.name) {
        setNotification({
          type: 'error',
          message: 'Property name is required',
        })
        setTimeout(() => setNotification(null), 5000)
        setLoading(false)
        return
      }

      // Prepare additional access data
      const additionalAccessData = additionalAccess
        .filter((a) => a.location && a.access_type && a.access_info)
        .map((a) => ({
          location: a.location,
          access_type: a.access_type,
          access_info: a.access_info,
        }))

      const notesToSave =
        notes.trim().length > 0
          ? [{ timestamp: new Date().toISOString(), note: notes.trim() }, ...propertyNotes]
          : propertyNotes

      // Build update object conditionally to avoid errors if column doesn't exist yet
      const updateData: any = {
        customer_id: propertyData.customer_id,
        name: propertyData.name,
        address: propertyData.address || null,
        city: propertyData.city || null,
        state: propertyData.state || null,
        zip_code: propertyData.zip || null,
        building_type: propertyData.building_type || null,
        // Also update property_type with building_type value (they should be in sync)
        property_type: propertyData.building_type || null,
        units: propertyData.units ? parseInt(propertyData.units) : null,
        stories: propertyData.stories ? parseInt(propertyData.stories) : null,
        access_type: propertyData.access_type || null,
        access_info: propertyData.access_info || null,
        additional_access: additionalAccessData.length > 0 ? additionalAccessData : null,
        payment_method: propertyData.payment_method || null,
        sales_tax_status: propertyData.sales_tax_status || null,
        default_assigned_crew_id: propertyData.default_assigned_crew_id || null,
        default_assigned_employee_id: propertyData.default_assigned_employee_id || null,
        notes: notesToSave,
      }

      // Only include address_line_2 if it has a value (column may not exist in DB yet)
      if (propertyData.address_line_2) {
        updateData.address_line_2 = propertyData.address_line_2
      }

      // Include selected contacts
      if (selectedContactIds.size > 0 && customerContacts.length > 0) {
        const selectedContacts = customerContacts.filter((c) => selectedContactIds.has(c.id))
        updateData.contacts = selectedContacts
      } else {
        updateData.contacts = []
      }

      // Update property
      const { error: updateError } = await supabase
        .from('properties')
        .update(updateData)
        .eq('id', id)

      if (updateError) throw updateError

      try {
        const { geocodeAddress, savePropertyCoordinates } = await import('../lib/geocodeProperty')
        let coords = await geocodeAddress({
          address: propertyData.address,
          city: propertyData.city,
          state: propertyData.state,
          zip_code: propertyData.zip,
        })
        if (!coords) {
          const lat = Number(manualLat)
          const lng = Number(manualLng)
          if (Number.isFinite(lat) && Number.isFinite(lng)) {
            await savePropertyCoordinates(id!, lat, lng, 'Manual entry')
          } else {
            setCoordsRequired(true)
            throw new Error(
              "Couldn't find exact coordinates for this address. Enter latitude/longitude manually and save again."
            )
          }
        } else {
          setGeocodePreview(coords)
          await savePropertyCoordinates(
            id!,
            coords.latitude,
            coords.longitude,
            coords.matched_address || null
          )
          // also stamp source/accuracy via geocodeAndSaveProperty path fields
          await supabase
            .from('properties')
            .update({
              geocode_source: coords.provider || 'geocode',
              geocode_accuracy: coords.accuracy || 'rooftop',
              geocode_matched_address: coords.matched_address || null,
            })
            .eq('id', id!)
        }
      } catch {
        /* non-fatal */
      }

      try {
        await reapplyAssignmentDefaultsForProperty(id!)
      } catch (reapplyErr) {
        console.warn('Property saved but job assignment reapply failed:', reapplyErr)
      }

      const currentSnapshot = buildPropertySnapshot(
        propertyData,
        notesToSave,
        selectedContactIds.size
      )
      const changes = diffSnapshots(
        initialSnapshotRef.current || {},
        currentSnapshot,
        PROPERTY_UPDATE_LABELS
      )

      await logActivity({
        activity_type: ActivityTypes.PROPERTY_UPDATED,
        entity_type: 'property',
        entity_id: id!,
        description: buildUpdateDescription(`Property "${propertyData.name}"`, changes),
        metadata: { changes },
      })

      initialSnapshotRef.current = currentSnapshot
      setPropertyNotes(notesToSave)
      setNotes('')

      setNotification({
        type: 'success',
        message: 'Property updated successfully!',
      })
      setTimeout(() => {
        setNotification(null)
        navigate('/properties')
      }, 2000)
    } catch (error) {
      console.error('Error updating property:', error)
      setNotification({
        type: 'error',
        message: 'Error updating property: ' + (error as any).message,
      })
      setTimeout(() => setNotification(null), 5000)
    } finally {
      setLoading(false)
    }
  }

  return (
    <MobilePage className="max-w-4xl mx-auto pb-28 lg:pb-8">
      <MobilePageHeader
        sticky
        title="Edit Property"
        subtitle="Update property information"
        onBack={() => navigate('/properties')}
        backLabel="Back to Properties"
      />

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Tab Navigation */}
          <div className={mobile.surface}>
            <div className="sticky top-[3.25rem] z-10 flex overflow-x-auto scrollbar-none border-b border-gray-200 bg-white/95 backdrop-blur-md">
              <button
                type="button"
                onClick={() => setActiveTab('address')}
                className={`px-4 sm:px-5 min-h-[48px] flex items-center whitespace-nowrap text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'address'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                Address
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('info')}
                className={`px-4 sm:px-5 min-h-[48px] flex items-center whitespace-nowrap text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'info'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                Info
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('payment')}
                className={`px-4 sm:px-5 min-h-[48px] flex items-center whitespace-nowrap text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'payment'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                Payment
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('contacts')}
                className={`px-4 sm:px-5 min-h-[48px] flex items-center whitespace-nowrap text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'contacts'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                Contacts
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('notes')}
                className={`px-4 sm:px-5 min-h-[48px] flex items-center whitespace-nowrap text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'notes'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                Notes
              </button>
            </div>
            <div className="p-4 sm:p-6">
              {/* Address Tab */}
              {activeTab === 'address' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Customer *
                      </label>
                      <select
                        required
                        value={propertyData.customer_id}
                        onChange={(e) =>
                          setPropertyData({ ...propertyData, customer_id: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Select a customer</option>
                        {customers.map((customer) => (
                          <option key={customer.id} value={customer.id}>
                            {customer.company_name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Property Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={propertyData.name}
                        onChange={(e) => setPropertyData({ ...propertyData, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="e.g., Main Office Building"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Address *
                      </label>
                      <AddressAutocomplete
                        value={propertyData.address}
                        onChange={handleAddressChange}
                        onAddressSelect={handleAddressSelect}
                        placeholder="Enter full address"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Apt/Suite # <span className="text-gray-400 font-normal">(Optional)</span>
                      </label>
                      <input
                        type="text"
                        value={propertyData.address_line_2}
                        onChange={(e) =>
                          setPropertyData({ ...propertyData, address_line_2: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Apartment, Suite, Unit, etc."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">City *</label>
                      <input
                        type="text"
                        required
                        value={propertyData.city}
                        onChange={(e) => setPropertyData({ ...propertyData, city: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        State *
                      </label>
                      <input
                        type="text"
                        required
                        value={propertyData.state}
                        onChange={(e) =>
                          setPropertyData({ ...propertyData, state: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        ZIP Code *
                      </label>
                      <input
                        type="text"
                        required
                        value={propertyData.zip}
                        onChange={(e) => setPropertyData({ ...propertyData, zip: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Info Tab */}
              {activeTab === 'info' && (
                <div className="space-y-6">
                  {/* Building Info */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Building Type
                      </label>
                      <select
                        value={propertyData.building_type}
                        onChange={(e) =>
                          setPropertyData({ ...propertyData, building_type: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Select type</option>
                        {buildingTypes.map((type) => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Units</label>
                      <input
                        type="number"
                        value={propertyData.units}
                        onChange={(e) =>
                          setPropertyData({ ...propertyData, units: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="e.g., 10"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Stories
                      </label>
                      <input
                        type="number"
                        value={propertyData.stories}
                        onChange={(e) =>
                          setPropertyData({ ...propertyData, stories: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="e.g., 3"
                      />
                    </div>
                  </div>

                  {/* Access Info */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900">Access Information</h3>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Access Type
                      </label>
                      <select
                        value={propertyData.access_type}
                        onChange={(e) =>
                          setPropertyData({ ...propertyData, access_type: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Select access type</option>
                        {accessTypes.map((type) => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Access Info
                      </label>
                      <input
                        type="text"
                        value={propertyData.access_info}
                        onChange={(e) =>
                          setPropertyData({ ...propertyData, access_info: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="e.g., Code: 1234 or Location: Under mat"
                      />
                    </div>

                    {/* Additional Access */}
                    <div className="space-y-4">
                      <h4 className="text-sm font-semibold text-gray-900">Additional Access</h4>
                      {additionalAccess.map((access) => (
                        <div key={access.id} className="grid grid-cols-1 md:grid-cols-4 gap-4">
                          <input
                            type="text"
                            value={access.location}
                            onChange={(e) =>
                              updateAdditionalAccess(access.id, 'location', e.target.value)
                            }
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Location (e.g., Basement)"
                          />
                          <select
                            value={access.access_type}
                            onChange={(e) =>
                              updateAdditionalAccess(access.id, 'access_type', e.target.value)
                            }
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="">Select type</option>
                            {accessTypes.map((type) => (
                              <option key={type} value={type}>
                                {type}
                              </option>
                            ))}
                          </select>
                          <input
                            type="text"
                            value={access.access_info}
                            onChange={(e) =>
                              updateAdditionalAccess(access.id, 'access_info', e.target.value)
                            }
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Access info"
                          />
                          <button
                            type="button"
                            onClick={() => removeAdditionalAccess(access.id)}
                            className="px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                          >
                            <XIcon className="w-5 h-5" />
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={addAdditionalAccess}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-gray-400 transition-colors"
                      >
                        <PlusIcon className="w-5 h-5" />
                        Add Additional Access
                      </button>
                    </div>
                  </div>

                  <div className="border-t border-gray-200 pt-6">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Default Assignment</h3>
                    <PropertyAssignmentFields
                      value={{
                        default_assigned_crew_id: propertyData.default_assigned_crew_id,
                        default_assigned_employee_id: propertyData.default_assigned_employee_id,
                      }}
                      onChange={(assignment) =>
                        setPropertyData({ ...propertyData, ...assignment })
                      }
                    />
                  </div>
                </div>
              )}

              {/* Payment Tab */}
              {activeTab === 'payment' && (
                <PropertyPaymentFields
                  value={{
                    payment_method: propertyData.payment_method,
                    sales_tax_status: propertyData.sales_tax_status,
                  }}
                  onChange={(payment) => setPropertyData({ ...propertyData, ...payment })}
                  customerDefaults={customerDefaults}
                />
              )}

              {/* Contacts Tab */}
              {activeTab === 'contacts' && (
                <div className="space-y-4">
                  {!propertyData.customer_id ? (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <p className="text-yellow-800">
                        Please select a customer first to view and select contacts.
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="flex justify-between items-center mb-4">
                        <p className="text-sm text-gray-600">
                          Select contacts from the parent company that are specific to this property:
                        </p>
                        <button
                          type="button"
                          onClick={() => setShowCreateContactModal(true)}
                          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          <PlusIcon className="w-4 h-4" />
                          Add New Contact
                        </button>
                      </div>
                      {customerContacts.length === 0 ? (
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                          <p className="text-gray-600">No contacts found for this customer.</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {customerContacts.map((contact) => (
                            <label
                              key={contact.id}
                              className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={selectedContactIds.has(contact.id)}
                                onChange={() => toggleContactSelection(contact.id)}
                                className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                              />
                              <div className="flex-1">
                                <div className="font-medium text-gray-900">{contact.name}</div>
                                {contact.title && (
                                  <div className="text-sm text-gray-600">{contact.title}</div>
                                )}
                                <div className="text-sm text-gray-500 mt-1">
                                  {contact.phone && <span>Phone: {contact.phone}</span>}
                                  {contact.email && (
                                    <span className="ml-3">Email: {contact.email}</span>
                                  )}
                                </div>
                              </div>
                            </label>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Notes Tab */}
              {activeTab === 'notes' && (
                <div className="space-y-4">
                  <div className="flex gap-2">
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && e.ctrlKey) {
                          addNote()
                        }
                      }}
                      rows={3}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Add a note... (Ctrl+Enter to submit)"
                    />
                    <button
                      type="button"
                      onClick={addNote}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors self-start"
                    >
                      Add Note
                    </button>
                  </div>
                  <div className="space-y-3">
                    {propertyNotes.length > 0 ? (
                      propertyNotes
                        .slice()
                        .reverse()
                        .map((note) => (
                          <div
                            key={note.timestamp}
                            className="flex gap-3 bg-gray-50 rounded-lg p-3"
                          >
                            <div className="flex-1">
                              <p className="text-sm text-gray-700">{note.note}</p>
                              <p className="text-xs text-gray-500 mt-1">
                                {new Date(note.timestamp).toLocaleString()}
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeNote(note.timestamp)}
                              className="p-1 hover:bg-red-50 rounded transition-colors"
                            >
                              <XIcon className="w-4 h-4 text-red-600" />
                            </button>
                          </div>
                        ))
                    ) : (
                      <p className="text-sm text-gray-500 italic">No notes yet</p>
                    )}
                  </div>

                  {/* Activity Section */}
                  <div className="border-t border-gray-200 pt-4 mt-4">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">Activity</h4>
                    {activities.length > 0 ? (
                      <div className="space-y-2">
                        {activities.map((activity) => (
                          <div key={activity.id} className="bg-gray-50 rounded-lg p-3">
                            <p className="text-sm text-gray-700">{activity.description}</p>
                            <p className="text-xs text-gray-500 mt-1">
                              {formatActivityMeta(activity)}
                            </p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 italic">No recent activity</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Submit Buttons */}
          <div className="native-action-bar flex items-center justify-between gap-3 max-w-4xl mx-auto lg:rounded-xl lg:border lg:border-gray-200">
            <button
              type="button"
              onClick={() => navigate('/properties')}
              className={mobile.secondaryBtn}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className={mobile.primaryBtn}
            >
              <span className="sm:hidden">{loading ? 'Saving...' : 'Save'}</span>
              <span className="hidden sm:inline">{loading ? 'Updating...' : 'Update Property'}</span>
            </button>
          </div>
        </form>

        {/* Create Contact Modal */}
        {showCreateContactModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Add New Contact</h3>
              <p className="text-sm text-gray-600 mb-4">
                This contact will be added to the customer and automatically selected for this property.
              </p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    value={newContact.name}
                    onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Contact name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title
                  </label>
                  <input
                    type="text"
                    value={newContact.title}
                    onChange={(e) => setNewContact({ ...newContact, title: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., Manager, Director"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={newContact.phone}
                    onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Phone number"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cell
                  </label>
                  <input
                    type="tel"
                    value={newContact.cell}
                    onChange={(e) => setNewContact({ ...newContact, cell: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Cell number"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={newContact.email}
                    onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Email address"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateContactModal(false)
                    setNewContact({ name: '', title: '', phone: '', cell: '', email: '' })
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  disabled={creatingContact}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleCreateContact}
                  className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={creatingContact}
                >
                  {creatingContact ? 'Adding...' : 'Add Contact'}
                </button>
              </div>
            </div>
          </div>
        )}

      {/* Notification Popup Modal */}
      {notification && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div
            className={`bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl ${
              notification.type === 'success'
                ? 'border-2 border-green-500'
                : 'border-2 border-red-500'
            }`}
          >
            <div className="flex items-center gap-4">
              <div
                className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${
                  notification.type === 'success'
                    ? 'bg-green-100'
                    : 'bg-red-100'
                }`}
              >
                {notification.type === 'success' ? (
                  <svg
                    className="w-6 h-6 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-6 h-6 text-red-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                )}
              </div>
              <div className="flex-1">
                <p
                  className={`text-lg font-medium ${
                    notification.type === 'success' ? 'text-green-800' : 'text-red-800'
                  }`}
                >
                  {notification.message}
                </p>
              </div>
              <button
                onClick={() => setNotification(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
    </MobilePage>
  )
}